import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const REQUIRED_FIELDS = ['address', 'price', 'bedrooms', 'bathrooms', 'property_type'];
const OPTIONAL_FIELDS = ['description', 'features', 'photos', 'suburb', 'state', 'postcode'];

const CSVUploadZone = ({ onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseResults, setParseResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const parseCSV = (text) => {
    const lines = text?.trim()?.split('\n');
    if (lines?.length < 2) return { success: [], errors: [{ row: 1, message: 'CSV file is empty or has no data rows' }] };
    const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase()?.replace(/\s+/g, '_'));
    const success = [];
    const errors = [];
    lines?.slice(1)?.forEach((line, idx) => {
      const row = idx + 2;
      const values = line?.split(',')?.map(v => v?.trim());
      const record = {};
      headers?.forEach((h, i) => { record[h] = values?.[i] || ''; });
      const missing = REQUIRED_FIELDS?.filter(f => !record?.[f]);
      if (missing?.length > 0) {
        errors?.push({ row, message: `Missing required fields: ${missing?.join(', ')}`, data: record });
      } else if (isNaN(parseFloat(record?.price))) {
        errors?.push({ row, message: 'Invalid price format — must be a number', data: record });
      } else {
        success?.push({ row, data: record });
      }
    });
    return { success, errors };
  };

  const processFile = (file) => {
    if (!file || !file?.name?.endsWith('.csv')) {
      setParseResults({ success: [], errors: [{ row: 0, message: 'Please upload a valid .csv file' }] });
      return;
    }
    setUploadedFile(file);
    setIsProcessing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      setTimeout(() => {
        const results = parseCSV(e?.target?.result);
        setParseResults(results);
        setIsProcessing(false);
        if (results?.success?.length > 0) {
          onUploadComplete?.(results?.success?.map(r => r?.data));
        }
      }, 800);
    };
    reader?.readAsText(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    processFile(file);
  };

  const handleFileChange = (e) => {
    processFile(e?.target?.files?.[0]);
  };

  const downloadTemplate = () => {
    const headers = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS]?.join(',');
    const sample = '123 Main St,750000,3,2,house,Beautiful family home,Pool|Garage,https://example.com/photo.jpg,Sydney,NSW,2000';
    const blob = new Blob([headers + '\n' + sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'property_upload_template.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setParseResults(null);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {/* Template Download */}
      <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
        <div className="flex items-center gap-3">
          <Icon name="Download" size={18} color="#00D4FF" />
          <div>
            <p className="text-white text-sm font-medium">Download CSV Template</p>
            <p className="text-slate-400 text-xs">Includes all required and optional fields</p>
          </div>
        </div>
        <button onClick={downloadTemplate} className="px-4 py-2 rounded-lg text-sm font-medium text-cyan-400 border border-cyan-400/30 hover:bg-cyan-400/10 transition-colors">
          Download
        </button>
      </div>
      {/* Required Fields Info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-1"><Icon name="AlertCircle" size={12} color="#F87171" /> Required Fields</p>
          <div className="space-y-1">
            {REQUIRED_FIELDS?.map(f => (
              <p key={f} className="text-xs text-slate-300 font-mono">{f}</p>
            ))}
          </div>
        </div>
        <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs font-semibold text-slate-400 mb-2 flex items-center gap-1"><Icon name="Info" size={12} color="#94A3B8" /> Optional Fields</p>
          <div className="space-y-1">
            {OPTIONAL_FIELDS?.map(f => (
              <p key={f} className="text-xs text-slate-400 font-mono">{f}</p>
            ))}
          </div>
        </div>
      </div>
      {/* Drop Zone */}
      {!uploadedFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef?.current?.click()}
          className={`relative flex flex-col items-center justify-center p-10 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
            isDragging ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/20 hover:border-cyan-400/50 hover:bg-white/5'
          }`}
        >
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(0,212,255,0.1)' }}>
            <Icon name="Upload" size={26} color="#00D4FF" />
          </div>
          <p className="text-white font-semibold mb-1">{isDragging ? 'Drop your CSV here' : 'Drag & drop your CSV file'}</p>
          <p className="text-slate-400 text-sm">or click to browse files</p>
          <p className="text-slate-500 text-xs mt-3">.csv files only • Max 10MB</p>
        </div>
      ) : (
        <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-400/10 flex items-center justify-center">
                <Icon name="FileText" size={18} color="#00D4FF" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{uploadedFile?.name}</p>
                <p className="text-slate-400 text-xs">{(uploadedFile?.size / 1024)?.toFixed(1)} KB</p>
              </div>
            </div>
            <button onClick={resetUpload} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Icon name="X" size={16} color="#94A3B8" />
            </button>
          </div>
        </div>
      )}
      {/* Processing */}
      {isProcessing && (
        <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-cyan-400 text-sm">Validating CSV data...</p>
        </div>
      )}
      {/* Validation Results */}
      {parseResults && !isProcessing && (
        <div className="space-y-3">
          {parseResults?.success?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="CheckCircle" size={16} color="#22C55E" />
                <p className="text-green-400 font-semibold text-sm">{parseResults?.success?.length} properties ready to import</p>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {parseResults?.success?.slice(0, 5)?.map((item) => (
                  <div key={item?.row} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500">Row {item?.row}</span>
                    <span className="text-slate-300">{item?.data?.address}</span>
                    <span className="ml-auto text-green-400">${parseFloat(item?.data?.price)?.toLocaleString()}</span>
                  </div>
                ))}
                {parseResults?.success?.length > 5 && (
                  <p className="text-slate-500 text-xs">+{parseResults?.success?.length - 5} more properties</p>
                )}
              </div>
            </div>
          )}
          {parseResults?.errors?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="AlertTriangle" size={16} color="#EF4444" />
                <p className="text-red-400 font-semibold text-sm">{parseResults?.errors?.length} rows have errors</p>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {parseResults?.errors?.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-slate-500 shrink-0">Row {err?.row}</span>
                    <span className="text-red-300">{err?.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVUploadZone;
